import { UpdateIcon } from "@radix-ui/react-icons";
import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";
import { useNavigate } from "react-router-dom";

import { IHospital } from "../../../@types/admin/AdminHospital";
import { TableSorted } from "../../../components/TableSorted";
import {
  useHospitals,
  useTenants,
} from "../../../hooks/queries/admin/hospitals";
import { useModal } from "../../../hooks/useModal";
import { RowActions } from "../Patients/AdminPatients.styles";
import {
  ErrorMessage,
  FilterButton,
  FiltersActions,
  FiltersContainer,
  PageContainer,
  PageHeader,
  PageTitle,
  RowAction,
  SearchInput,
  StatusBadge,
  TableContainer,
  TableLoadingState,
  TablePagination,
  TableWrapper,
  useMockPagination,
} from "../shared";
import { AddHospitalModal } from "./AddHospitalModal";
import { SyncTenantIdsModal } from "./SyncTenantIdsModal";

/** A hospital row with its tenant resolved, so search covers the name too. */
type IHospitalRow = IHospital & { tenantName: string };

const SEARCH_FIELDS: (keyof IHospitalRow)[] = [
  "id",
  "name",
  "code",
  "timezone",
  "tenantName",
];

export function AdminHospitalsList() {
  document.title = "Hospitals - Admin";

  const navigate = useNavigate();

  const { openModal, closeModal } = useModal();
  const { hospitals, isLoading, isError } = useHospitals();
  const { tenants } = useTenants();

  const rows: IHospitalRow[] = useMemo(() => {
    // Retool resolved this per cell with a `.find()` in a valueOverride; a
    // lookup map avoids re-scanning the tenant list for every row.
    const tenantNamesById = new Map(
      tenants.map((tenant) => [tenant.id, tenant.name])
    );

    return hospitals.map((hospital) => ({
      ...hospital,
      tenantName: hospital.tenantId
        ? tenantNamesById.get(hospital.tenantId) ?? hospital.tenantId
        : "",
    }));
  }, [hospitals, tenants]);

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({ data: rows, searchFields: SEARCH_FIELDS });

  const handleAddClick = () => {
    openModal(
      <AddHospitalModal onCreated={setSearchTerm} onClose={closeModal} />,
      { width: "480px", showCloseButton: true }
    );
  };

  const handleSyncClick = (hospital: IHospital) => {
    openModal(<SyncTenantIdsModal hospital={hospital} onClose={closeModal} />, {
      width: "480px",
      showCloseButton: true,
    });
  };

  const columns: ColumnDef<IHospitalRow, string>[] = useMemo(
    () => [
      {
        accessorKey: "id",
        header: "ID",
        cell: ({ row }) => <span>{row.original.id}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "name",
        header: "Name",
        cell: ({ row }) => <span>{row.original.name}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "code",
        header: "Code",
        cell: ({ row }) => <span>{row.original.code}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "timezone",
        header: "Timezone",
        cell: ({ row }) => <span>{row.original.timezone}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "tenantName",
        header: "Tenant",
        cell: ({ row }) => <span>{row.original.tenantName || "-"}</span>,
        enableSorting: true,
      },
      {
        id: "publish",
        header: "Publish",
        // TableSorted skips the cell renderer for a falsy value, so the
        // label doubles as the accessor (and the sort key).
        accessorFn: (hospital) =>
          hospital.publish ? "Published" : "Unpublished",
        cell: ({ row }) => (
          <StatusBadge status={row.original.publish ? "active" : "inactive"}>
            {row.original.publish ? "Published" : "Unpublished"}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        id: "actions",
        header: "",
        enableSorting: false,
        cell: ({ row }) => (
          <RowActions>
            <RowAction
              label="Sync tenant IDs"
              icon={<UpdateIcon />}
              onClick={() => {
                handleSyncClick(row.original);
              }}
            />
          </RowActions>
        ),
      },
    ],
    [rows]
  );

  return (
    <PageContainer>
      <PageHeader>
        <PageTitle>Hospitals</PageTitle>
      </PageHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by name, code, timezone, or tenant..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <FiltersActions>
          <FilterButton variant="primary" onClick={handleAddClick}>
            Add hospital
          </FilterButton>
        </FiltersActions>
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load the hospitals. Try refreshing the page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-hospitals"]]}>
          <TableSorted
            columns={columns}
            data={items}
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
            onRowClick={(hospital) =>
              navigate(`/admin/hospitals/${hospital.id}`)
            }
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "hospitals",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PageContainer>
  );
}
